// Cross-window material live-reload bridge.
//
// Material/texture/mapping edits drive the live avatar via the global
// `window.__RELOAD_MATERIALS__` (installed by AvatarModel — the ONLY window
// that owns the avatar). A window with no avatar would persist an edit to the
// server and never refresh the character. This bus closes that gap: an edit
// reloads the avatar in the current window (if it has one) AND broadcasts a
// reload request so any avatar-owning window reloads too.
//
// It was written for the ?material_popup= second app boot, which is gone — the
// material graph pops out as a PORTAL of this instance now, sharing this
// window's globals. The bus is kept because a SECOND TAB open on the same
// character is still a real case and still has its own module instances.
//
// One module-level BroadcastChannel is shared for post + subscribe. A channel
// never receives its own posts, so reloadMaterialsEverywhere() calling the
// local reload directly + posting on the same instance can't double-fire the
// subscriber in the same window.

const CHANNEL = 'aos_material_reload'

let _ch = null
function chan() {
  if (typeof BroadcastChannel === 'undefined') return null
  if (!_ch) {
    _ch = new BroadcastChannel(CHANNEL)
    // Don't keep a Node test process / worker alive on this channel.
    _ch.unref?.()
  }
  return _ch
}

function reloadLocalAvatar() {
  if (typeof window !== 'undefined' && typeof window.__RELOAD_MATERIALS__ === 'function') {
    // __RELOAD_MATERIALS__ is async (re-fetch + rebuild). Return its promise so
    // callers can show an "applying" state until the avatar actually updates.
    return window.__RELOAD_MATERIALS__() ?? Promise.resolve()
  }
  return null
}

// Call after any persisted material / texture / material-mapping mutation.
// Reloads the avatar in this window (if present) and tells every other window
// to do the same. The receiver reloads its own active character (both windows
// share one character once the popup is opened with ?character=<id>), so no
// payload is needed.
export function reloadMaterialsEverywhere() {
  const p = reloadLocalAvatar()
  chan()?.postMessage({ type: 'reload_materials' })
  // Resolve once this window's avatar has rebuilt (null when this window has no
  // avatar — e.g. the undocked editor popup — so callers still get a promise).
  return p ?? Promise.resolve()
}

// Wire a window to honor cross-window reload requests. No-op in a window that
// has no avatar (the reload simply does nothing there). Returns an unsubscribe.
export function subscribeMaterialReload() {
  const c = chan()
  if (!c) return () => {}
  const onMsg = e => {
    if (e?.data?.type === 'reload_materials') reloadLocalAvatar()
  }
  c.addEventListener('message', onMsg)
  return () => c.removeEventListener('message', onMsg)
}
